/**
 * @fileoverview AR-924 — Status column of a step-2 row: the match/outcome
 * badge and, while that row's files are being sent, a progress bar.
 */

import { Badge, Progress, Stack } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import type { StagedItem } from './stagedItems'

type Translate = (key: string, options?: object) => string

const BADGE_SPACING = 6
const BAR_WIDTH = 120

function badgeOf(item: StagedItem, wholeClass: boolean, tr: Translate) {
  if (item.status === 'uploading') return { color: 'blue', label: tr('status_uploading', { percent: item.percent }) }
  if (item.status === 'uploaded') return { color: 'green', label: tr('status_uploaded') }
  if (item.status === 'failed') return { color: 'red', label: tr('status_upload_failed') }
  if (item.studentIds.length === 0) return { color: 'red', label: tr('needs_student') }
  if (wholeClass) return { color: 'indigo', label: tr('matched_whole_class') }
  if (item.studentIds.length > 1) {
    return { color: 'indigo', label: tr('matched_students', { count: item.studentIds.length }) }
  }
  if (item.matchedBy === 'name') return { color: 'green', label: tr('matched_by_name') }
  if (item.matchedBy === 'only') return { color: 'green', label: tr('matched_only_student') }
  return { color: 'indigo', label: tr('matched_by_you') }
}

interface MatchStatusCellProps {
  readonly item: StagedItem
  /** The item goes to every uploadable student — one badge instead of a count. */
  readonly wholeClass?: boolean
}

export function MatchStatusCell({ item, wholeClass = false }: MatchStatusCellProps) {
  const { t } = useTranslation()
  const tr: Translate = (key, options) => t(`task_tracker.upload_for_class.${key}`, options)
  const badge = badgeOf(item, wholeClass, tr)
  return (
    <Stack spacing={BADGE_SPACING} align="flex-start">
      <Badge variant="light" color={badge.color} radius="sm" size="sm" sx={{ textTransform: 'none', fontWeight: 600 }}>
        {badge.label}
      </Badge>
      {item.status === 'uploading' && (
        <Progress value={item.percent} size="sm" radius="xl" color="blue" animate sx={{ width: BAR_WIDTH }} />
      )}
    </Stack>
  )
}
